import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { useTranslation } from 'react-i18next';
import { OIGStatCard } from '@/modules/oig/components/OIGStatCard';
import {
  FileText,
  Upload,
  CheckCircle2,
  AlertTriangle,
  Clock,
  TrendingUp,
  ArrowRight,
  FileSpreadsheet,
  Truck,
  Network,
  FolderOpen,
  Activity,
  RefreshCw,
  Loader2 } from
'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell } from
'recharts';

import { oigAPI } from '@/shared/services/api';

const documentTypes = [
{ id: 'nfe', name: 'NF-e', icon: FileText },
{ id: 'cte', name: 'CT-e', icon: Truck },
{ id: 'edi', name: 'EDI', icon: Network },
{ id: 'csv', name: 'CSV/TXT', icon: FileSpreadsheet }];


export const OIGDashboard = () => {
  const { t } = useTranslation('oig');
  const [stats, setStats] = useState({
    totalDocuments: 0,
    pendingValidation: 0,
    processedToday: 0,
    asnGenerated: 0
  });
  const [documentStats, setDocumentStats] = useState([
  { name: 'NF-e', value: 0, color: '#16a34a' },
  { name: 'CT-e', value: 0, color: '#2563eb' },
  { name: 'EDI', value: 0, color: '#8b5cf6' },
  { name: 'CSV', value: 0, color: '#f97316' }]
  );
  const [weeklyData, setWeeklyData] = useState([]);
  const [documentCounts, setDocumentCounts] = useState({ nfe: 0, cte: 0, edi: 0, csv: 0 });
  const [recentImports, setRecentImports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [statsData, historyData, weeklyData, distributionData, countsData] = await Promise.all([
        oigAPI.getStats(),
        oigAPI.getImportHistory(5),
        oigAPI.getWeeklyChartData(),
        oigAPI.getDistributionChartData(),
        oigAPI.getDocumentCounts()]
        );

        if (statsData) {
          setStats({
            totalDocuments: statsData.total_documents || statsData.documents_processed || 0,
            pendingValidation: statsData.pending_validation || statsData.asn_pending || 0,
            processedToday: statsData.processed_today || statsData.completed_today || 0,
            asnGenerated: statsData.asn_generated || statsData.asn_receiving || 0
          });
        }

        setRecentImports(historyData || []);
        setWeeklyData(weeklyData || []);
        setDocumentStats(distributionData || []);
        setDocumentCounts(countsData || { nfe: 0, cte: 0, edi: 0, csv: 0 });
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="oig-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">
            {t('dashboard.title', 'OIG - Gateway de Integração')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('dashboard.subtitle', 'Operational Integration Gateway - Ingestão de Documentos')}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/oig/nfe/upload">
            <Button className="gap-2 bg-cyan-600 hover:bg-cyan-700">
              <Upload className="h-4 w-4" />
              {t('dashboard.importDocuments', 'Importar Documentos')}
            </Button>
          </Link>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <OIGStatCard
          title={t('dashboard.stats.totalDocuments', 'Total de Documentos')}
          value={stats.totalDocuments}
          icon={FileText}
          color="cyan"
          subtitle={t('dashboard.stats.thisMonth', 'Este mês')} />
        
        <OIGStatCard
          title={t('dashboard.stats.pendingValidation', 'Pendente Validação')}
          value={stats.pendingValidation}
          icon={Clock}
          color="amber"
          subtitle={t('dashboard.stats.awaitingProcessing', 'Aguardando processamento')} />
        
        <OIGStatCard
          title={t('dashboard.stats.processedToday', 'Processados Hoje')}
          value={stats.processedToday}
          icon={CheckCircle2}
          color="green"
          trend={{ type: 'up', value: 12 }} />
        
        <OIGStatCard
          title={t('dashboard.stats.asnGenerated', 'ASNs Gerados')}
          value={stats.asnGenerated}
          icon={FolderOpen}
          color="blue"
          subtitle={t('dashboard.stats.linkedToWms', 'Vinculados ao WMS')} />
        
      </div>

      {/* Document Type Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Link to="/oig/nfe">
          <Card className="card-elevated hover:shadow-lg transition-all cursor-pointer border-l-4 border-l-emerald-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-emerald-500/10">
                  <FileText className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <p className="font-semibold">{t("nfe", "NF-e")}</p>
                  <p className="text-sm text-muted-foreground">{t("notaFiscalEletrnica", "Nota Fiscal Eletr\xF4nica")}</p>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-2xl font-bold">{documentCounts.nfe}</span>
                <Badge className="bg-emerald-100 text-emerald-700">{t("ativo", "Ativo")}</Badge>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/oig/cte">
          <Card className="card-elevated hover:shadow-lg transition-all cursor-pointer border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-blue-500/10">
                  <Truck className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold">{t("cte", "CT-e")}</p>
                  <p className="text-sm text-muted-foreground">{t("conhecimentoTransporte", "Conhecimento Transporte")}</p>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-2xl font-bold">{documentCounts.cte}</span>
                <Badge className="bg-blue-100 text-blue-700">{t("ativo", "Ativo")}</Badge>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/oig/edi">
          <Card className="card-elevated hover:shadow-lg transition-all cursor-pointer border-l-4 border-l-violet-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-violet-500/10">
                  <Network className="h-6 w-6 text-violet-600" />
                </div>
                <div>
                  <p className="font-semibold">{t("edi", "EDI")}</p>
                  <p className="text-sm text-muted-foreground">{t("intercmbioEletrnico", "Interc\xE2mbio Eletr\xF4nico")}</p>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-2xl font-bold">{documentCounts.edi}</span>
                <Badge className="bg-violet-100 text-violet-700">{t("ativo", "Ativo")}</Badge>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/oig/csv">
          <Card className="card-elevated hover:shadow-lg transition-all cursor-pointer border-l-4 border-l-orange-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-orange-500/10">
                  <FileSpreadsheet className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="font-semibold">{t("csvtxt", "CSV/TXT")}</p>
                  <p className="text-sm text-muted-foreground">{t("importaoEmMassa", "Importa\xE7\xE3o em Massa")}</p>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-2xl font-bold">{documentCounts.csv}</span>
                <Badge className="bg-orange-100 text-orange-700">{t("ativo", "Ativo")}</Badge>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Weekly Activity */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-muted-foreground" />{t("documentosPorDia", "Documentos por Dia")}

            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }} />
                
                <Area type="monotone" dataKey="nfe" stackId="1" stroke="#16a34a" fill="#16a34a" fillOpacity={0.6} name="NF-e" />
                <Area type="monotone" dataKey="cte" stackId="1" stroke="#2563eb" fill="#2563eb" fillOpacity={0.6} name="CT-e" />
                <Area type="monotone" dataKey="edi" stackId="1" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.6} name="EDI" />
                <Area type="monotone" dataKey="csv" stackId="1" stroke="#f97316" fill="#f97316" fillOpacity={0.6} name="CSV" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Document Distribution */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-muted-foreground" />{t("distribuioPorTipo", "Distribui\xE7\xE3o por Tipo")}

            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={documentStats}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  
                  {documentStats.map((entry, index) =>
                  <Cell key={`cell-${index}`} fill={entry.color} />
                  )}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Recent Imports */}
      <Card className="card-elevated">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-muted-foreground" />
            {t('dashboard.recentImports.title', 'Importações Recentes')}
          </CardTitle>
          <Link to="/oig/history">
            <Button variant="ghost" size="sm">
              {t('dashboard.recentImports.viewHistory', 'Ver Histórico')}
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {recentImports.map((item) =>
            <div key={item.id} className="flex items-center justify-between p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${item.tipo === 'NF-e' ? 'bg-emerald-100 text-emerald-600' :
                item.tipo === 'CT-e' ? 'bg-blue-100 text-blue-600' :
                item.tipo === 'EDI' ? 'bg-violet-100 text-violet-600' :
                'bg-orange-100 text-orange-600'}`
                }>
                    {item.tipo === 'NF-e' && <FileText className="h-4 w-4" />}
                    {item.tipo === 'CT-e' && <Truck className="h-4 w-4" />}
                    {item.tipo === 'EDI' && <Network className="h-4 w-4" />}
                    {item.tipo === 'CSV' && <FileSpreadsheet className="h-4 w-4" />}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{item.arquivo}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(item.dataImportacao).toLocaleString('pt-BR')} • {item.registros}{t("registros", "registro(s)")}
                  </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {item.asnGerado &&
                <Badge variant="outline" className="text-xs">
                      {item.asnGerado}
                    </Badge>
                }
                  <Badge className={
                item.status === 'Sucesso' ? 'bg-emerald-100 text-emerald-700' :
                item.status === 'Erro' ? 'bg-red-100 text-red-700' :
                'bg-amber-100 text-amber-700'
                }>
                    {item.status === 'Sucesso' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                    {item.status === 'Erro' && <AlertTriangle className="h-3 w-3 mr-1" />}
                    {item.status}
                  </Badge>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>);

};